/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/* =========================================================================
 * MaestroCare™ Protection Program  — v2 (hardened)
 * -------------------------------------------------------------------------
 * Design rules this file now enforces:
 *
 *  1. SINGLE SOURCE OF TRUTH. The checked radio is the only state. Nothing is
 *     read from form.dataset at add-to-cart time, so a DOM re-render can never
 *     leave a stale plan behind.
 *  2. ATOMIC. The warranty properties on the parent line and the paid warranty
 *     line item are written in the SAME /cart/add.js request. There is no code
 *     path that can record a warranty claim without also adding the charge.
 *  3. SELF-HEALING. A reconciliation pass runs on cart load, after every cart
 *     mutation, and immediately before checkout. If a warranty line has gone
 *     missing, the claim is stripped from the parent line rather than shipping
 *     an unfunded warranty. Orphan warranty lines are removed.
 *  4. LOUD. Failures surface to the shopper instead of console.error only.
 * ========================================================================= */

(function () {
  "use strict";

  var PARENT_REF_KEY = "_Warranty For Variant ID";
  var PROP_DURATION = "_Warranty Duration";
  var PROP_TIER = "_Warranty Tier";
  var PROP_SKU = "_Warranty SKU";
  var WARRANTY_PROP_KEYS = [PROP_DURATION, PROP_TIER, PROP_SKU];
  var DURATIONS = ["3", "5", "10"];

  // -----------------------------
  // Small helpers
  // -----------------------------
  function moneyFormatFromCents(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === "function") {
      return Shopify.formatMoney(cents);
    }
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(cents / 100);
  }

  function getWarrantyConfig(formEl) {
    var cfgEl = formEl.querySelector('[id^="MBWarrantyConfig-"]');
    if (!cfgEl) return null;
    try {
      return JSON.parse(cfgEl.textContent);
    } catch (e) {
      return null;
    }
  }

  function readSelectedVariantFromForm(formEl) {
    var script =
      formEl.querySelector("variant-picker script[data-variant]") ||
      formEl.querySelector("script[data-variant]");
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  function getTierForPrice(priceDollars, tiers) {
    for (var i = 0; i < tiers.length; i++) {
      var t = tiers[i];
      var isLast = i === tiers.length - 1;
      if ((priceDollars >= t.min && priceDollars < t.max) || (isLast && priceDollars >= t.min && priceDollars <= t.max)) {
        return t;
      }
    }
    return null;
  }

  function getWrapper(formEl) {
    return formEl ? formEl.querySelector("[data-mb-warranty-wrapper]") : null;
  }

  function getFormForElement(el) {
    return el ? el.closest('form[action*="/cart/add"]') : null;
  }

  /* Only non-blank line item properties are ever submitted. */
  function extractLineItemProperties(formEl) {
    var fd = new FormData(formEl);
    var props = {};
    fd.forEach(function (value, key) {
      var m = key.match(/^properties\[(.+)\]$/);
      if (!m) return;
      if (value === null || String(value).trim() === "") return;
      props[m[1]] = value;
    });
    return props;
  }

  // -----------------------------
  // Selection state (the radio IS the state)
  // -----------------------------
  function setSelectedLabel(formEl, html) {
    var el = formEl.querySelector("[data-mb-warranty-selected]");
    if (el) el.innerHTML = html;
  }

  function clearWarrantySelection(formEl) {
    formEl.dataset.mbWarrantyDuration = "";
    var noneRadio = formEl.querySelector('input[name="mb_warranty_plan"][value="none"]');
    if (noneRadio) noneRadio.checked = true;
    DURATIONS.concat(["none"]).forEach(function (d) {
      var r = formEl.querySelector('input[name="mb_warranty_plan"][value="' + d + '"]');
      if (r && d !== "none") r.checked = false;
    });
    setSelectedLabel(formEl, "None");
  }

  /**
   * The ONLY function that decides what warranty (if any) is selected.
   * Reads the checked radio and the price span populated for the CURRENT variant.
   * Returns null when nothing valid is selected.
   */
  function readWarrantySelection(formEl) {
    var wrapper = getWrapper(formEl);
    if (!wrapper || wrapper.hidden) return null;

    var checked = wrapper.querySelector('input[name="mb_warranty_plan"]:checked');
    if (!checked || checked.value === "none") return null;

    var duration = checked.value;
    var span = wrapper.querySelector('[data-mb-warranty-price="' + duration + '"]');
    if (!span) return null;

    var variantId = parseInt(span.dataset.variantId || "", 10);
    if (!Number.isFinite(variantId) || variantId <= 0) return null;

    return {
      duration: duration,
      variantId: variantId,
      sku: span.dataset.sku || "",
      tierLabel: span.dataset.tierLabel || "",
      tierRange: span.dataset.tierRange || "",
      priceText: (span.textContent || "").replace("+ ", "").trim()
    };
  }

  function warrantyPropsForParent(selection) {
    var props = {};
    props[PROP_DURATION] = selection.duration + " Years";
    props[PROP_TIER] = selection.tierLabel + (selection.tierRange ? " (" + selection.tierRange + ")" : "");
    props[PROP_SKU] = selection.sku;
    return props;
  }

  // -----------------------------
  // Init / re-init
  // -----------------------------
  function initWarrantyForForm(formEl) {
    var wrapper = getWrapper(formEl);
    if (!wrapper) return;

    /* Remember the DURATION the shopper picked (never a variant id — the variant
       id must always be recomputed for the currently selected variant). */
    var desiredDuration = formEl.dataset.mbWarrantyDuration || "";

    /* ALWAYS clear first. Browsers restore radio state on reload/back-navigation
       but never restore JS state, so a restored radio must not be trusted. */
    clearWarrantySelection(formEl);

    var config = getWarrantyConfig(formEl);
    var variant = readSelectedVariantFromForm(formEl);
    if (!config || !variant) {
      wrapper.hidden = true;
      return;
    }

    var enabledMap = config.variantWarrantyEnabled || {};
    var warrantyAllowed = !!enabledMap[String(variant.id)];
    var priceCents = Number(variant.price || 0);
    var qualifies = warrantyAllowed && variant.available === true && priceCents > 0;
    if (!qualifies) {
      wrapper.hidden = true;
      return;
    }

    var tier = getTierForPrice(priceCents / 100, config.tiers || []);
    if (!tier) {
      wrapper.hidden = true;
      return;
    }

    /* Populate the option prices for THIS variant's tier. */
    var plans = config.plans || {};
    var available = {};
    DURATIONS.forEach(function (duration) {
      var list = plans[duration] || [];
      var match = null;
      for (var i = 0; i < list.length; i++) {
        if (Number(list[i].tier) === Number(tier.tier)) {
          match = list[i];
          break;
        }
      }
      var span = wrapper.querySelector('[data-mb-warranty-price="' + duration + '"]');
      var option = wrapper.querySelector('[data-mb-warranty-option="' + duration + '"]');
      var radio = wrapper.querySelector('input[name="mb_warranty_plan"][value="' + duration + '"]');

      if (!span) return;

      if (!match) {
        /* No plan sold for this tier/duration — remove the option entirely so it
           can never be selected and silently fail. */
        span.textContent = "";
        delete span.dataset.variantId;
        delete span.dataset.sku;
        if (option) option.hidden = true;
        if (radio) radio.disabled = true;
        return;
      }

      available[duration] = true;
      span.textContent = "+ " + moneyFormatFromCents(match.price);
      span.dataset.variantId = String(match.variantId);
      span.dataset.sku = match.sku || "";
      span.dataset.tierLabel = tier.label;
      span.dataset.tierRange = tier.range;
      if (option) option.hidden = false;
      if (radio) radio.disabled = false;

      var label = wrapper.querySelector('[data-mb-warranty-option-price="' + duration + '"]');
      if (label) label.textContent = moneyFormatFromCents(match.price);
    });

    wrapper.hidden = false;

    /* Re-apply the shopper's previously chosen duration for the NEW variant,
       recomputing the variant id + tier from scratch. If that duration isn't
       sold for the new tier, we stay on "None" (and the label says so). */
    if (desiredDuration && available[desiredDuration]) {
      applyDuration(formEl, desiredDuration);
    }
  }

  function applyDuration(formEl, duration) {
    var wrapper = getWrapper(formEl);
    if (!wrapper) return;

    if (duration === "none") {
      clearWarrantySelection(formEl);
      return;
    }

    var radio = wrapper.querySelector('input[name="mb_warranty_plan"][value="' + duration + '"]');
    if (!radio || radio.disabled) {
      clearWarrantySelection(formEl);
      return;
    }
    radio.checked = true;

    var selection = readWarrantySelection(formEl);
    if (!selection) {
      clearWarrantySelection(formEl);
      return;
    }

    formEl.dataset.mbWarrantyDuration = duration;
    setSelectedLabel(
      formEl,
      selection.duration + ' Years (<span class="mb-warranty__price-value">+' + selection.priceText + "</span>)"
    );
  }

  function initAllWarrantyBlocks(root) {
    (root || document).querySelectorAll('form[action*="/cart/add"]').forEach(function (formEl) {
      initWarrantyForForm(formEl);
    });
  }

  /* Delegated — survives every DOM replacement, no rebinding needed. */
  document.addEventListener("change", function (e) {
    var radio = e.target.closest ? e.target.closest('input[name="mb_warranty_plan"]') : null;
    if (!radio) return;
    var formEl = getFormForElement(radio);
    if (!formEl) return;
    applyDuration(formEl, radio.value);
  });

  document.addEventListener("click", function (e) {
    var clearBtn = e.target.closest ? e.target.closest("[data-mb-warranty-clear]") : null;
    if (!clearBtn) return;
    e.preventDefault();
    var formEl = getFormForElement(clearBtn);
    if (formEl) clearWarrantySelection(formEl);
  });

  document.addEventListener("DOMContentLoaded", function () {
    initAllWarrantyBlocks();
  });

  /* Quick-buy / quick-view modals render their form from a <template>. */
  document.addEventListener("modal:open", function () {
    setTimeout(function () {
      initAllWarrantyBlocks();
    }, 50);
  });

  /* Warehouse dispatches product:rerender on the FORM and it does not bubble. */
  document.addEventListener(
    "product:rerender",
    function (e) {
      var formEl = e.target;
      if (!formEl || !formEl.matches || !formEl.matches('form[action*="/cart/add"]')) return;
      setTimeout(function () {
        initWarrantyForForm(formEl);
      }, 0);
    },
    true
  );

  /* Warehouse passes the <input name="id"> ELEMENT as detail.formId (legacy form
     named-access), not a string — so resolve the form from the event target. */
  ["variant:change", "variant:changed"].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var formEl =
        (e.target && e.target.matches && e.target.matches('form[action*="/cart/add"]') && e.target) ||
        getFormForElement(e.target);
      if (formEl) initWarrantyForForm(formEl);
    });
  });

  // -----------------------------
  // Shopper-visible messaging
  // -----------------------------
  function showError(anchorEl, message) {
    var host = (anchorEl && anchorEl.closest(".product-form")) || document.body;
    var el = document.createElement("div");
    el.className = "cart__status-message";
    el.innerHTML = '<div class="alert alert--error"><p>' + message + "</p></div>";
    host.appendChild(el);
    setTimeout(function () {
      el.remove();
    }, 6000);
  }

  // -----------------------------
  // Add to cart — base + warranty in ONE atomic request
  // -----------------------------
  document.addEventListener(
    "click",
    async function (e) {
      var buyButtonEl = e.target.closest ? e.target.closest("buy-button") : null;
      if (!buyButtonEl) return;

      var formEl = getFormForElement(e.target);
      if (!formEl) return;

      /* Validate BEFORE cancelling the theme's own handler, so we can never
         leave the shopper with a dead Add-to-cart button. */
      var selection = readWarrantySelection(formEl);
      if (!selection) return; // no plan selected -> let the theme proceed normally

      var variant = readSelectedVariantFromForm(formEl);
      if (!variant) return;

      var baseVariantId = parseInt(
        (formEl.querySelector('[name="id"]') && formEl.querySelector('[name="id"]').value) || variant.id,
        10
      );
      if (!Number.isFinite(baseVariantId)) return;

      var qtyEl = formEl.querySelector('[name="quantity"]');
      var qty = parseInt((qtyEl && qtyEl.value) || "1", 10);
      if (!Number.isFinite(qty) || qty < 1) qty = 1;

      /* Everything validated — now take over. */
      e.preventDefault();
      e.stopPropagation();

      var wrapper = getWrapper(formEl);
      var productTitle =
        (wrapper && wrapper.dataset.mbProductTitle) ||
        (formEl.querySelector("[data-mb-product-title]") &&
          formEl.querySelector("[data-mb-product-title]").dataset.mbProductTitle) ||
        variant.name ||
        "";

      var baseProperties = Object.assign({}, extractLineItemProperties(formEl), warrantyPropsForParent(selection));

      var warrantyProps = {};
      warrantyProps["_Warranty For Product"] = productTitle;
      warrantyProps[PARENT_REF_KEY] = String(baseVariantId);
      warrantyProps["_Warranty Parent SKU"] = variant.sku || "";

      document.dispatchEvent(new CustomEvent("theme:loading:start"));

      var ok = false;
      try {
        var res = await fetch(window.routes.cartAddUrl + ".js", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({
            items: [
              { id: baseVariantId, quantity: qty, properties: baseProperties },
              { id: selection.variantId, quantity: qty, properties: warrantyProps }
            ]
          })
        });
        ok = res.ok;

        if (!ok) {
          var data = await res.json().catch(function () {
            return null;
          });
          showError(
            e.target,
            (data && (data.description || data.message)) ||
              "We could not add the MaestroCare plan to your cart. Please try again."
          );
        }
      } catch (err) {
        showError(e.target, "We could not add this item to your cart. Please try again.");
      } finally {
        buyButtonEl.dispatchEvent(
          new CustomEvent("product:added", {
            bubbles: true,
            detail: { button: e.target, variant: null, quantity: qty, productAdded: ok }
          })
        );
        document.dispatchEvent(new CustomEvent("theme:loading:end"));
      }
    },
    true
  );

  // =========================================================
  // Cart reconciliation — the safety net
  // =========================================================
  var mbReconcileLock = false;
  var mbReconcileTimer = null;

  function mbScheduleReconcile(delay) {
    if (mbReconcileTimer) clearTimeout(mbReconcileTimer);
    mbReconcileTimer = setTimeout(function () {
      mbReconcileCart().catch(function () {});
    }, typeof delay === "number" ? delay : 250);
  }

  function isWarrantyItem(item) {
    return !!(item && item.properties && item.properties[PARENT_REF_KEY]);
  }

  function pairKey(parentVariantId, warrantySku) {
    return String(parseInt(String(parentVariantId), 10)) + "::" + String(warrantySku || "").trim();
  }

  async function mbFetchCart() {
    var response = await fetch(window.routes.cartUrl + ".js", {
      method: "GET",
      credentials: "same-origin",
      headers: { "Cache-Control": "no-cache", "X-Requested-With": "XMLHttpRequest" }
    });
    if (!response.ok) throw new Error("Failed to fetch cart");
    return await response.json();
  }

  async function mbChangeLine(payload) {
    var response = await fetch(window.routes.cartChangeUrl + ".js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error("Failed to update cart line");
    return await response.json();
  }

  /**
   * Brings the cart back to a state where every warranty CLAIM has a matching
   * warranty CHARGE and vice versa.
   *
   * Applies one change per pass and re-fetches, because /cart/change.js is
   * line-index based and every mutation invalidates the indexes.
   *
   * Returns { changed: Boolean, costIncreased: Boolean }.
   */
  async function mbReconcileCart() {
    if (mbReconcileLock) return { changed: false, costIncreased: false };
    mbReconcileLock = true;

    var changed = false;
    var costIncreased = false;

    try {
      var cart = await mbFetchCart();

      for (var pass = 0; pass < 25; pass++) {
        var items = cart.items || [];

        /* What SHOULD be charged: sum of parent quantities per (variant, plan sku). */
        var expected = new Map();
        items.forEach(function (it) {
          if (isWarrantyItem(it)) return;
          var sku = it.properties && it.properties[PROP_SKU];
          if (!sku || String(sku).trim() === "") return;
          var k = pairKey(it.id, sku);
          expected.set(k, (expected.get(k) || 0) + it.quantity);
        });

        /* What IS charged. */
        var actual = new Map();
        items.forEach(function (it, i) {
          if (!isWarrantyItem(it)) return;
          var k = pairKey(it.properties[PARENT_REF_KEY], it.sku);
          if (!actual.has(k)) actual.set(k, { line: i + 1, quantity: it.quantity });
          else actual.get(k).quantity += it.quantity;
        });

        var action = null;
        var increases = false;

        /* 1. Orphan or mis-quantified warranty lines. */
        actual.forEach(function (info, k) {
          if (action) return;
          var want = expected.has(k) ? expected.get(k) : 0;
          if (info.quantity !== want) {
            action = { line: info.line, quantity: want };
            increases = want > info.quantity;
          }
        });

        /* 2. Parent lines claiming a plan that is not being charged for.
              Strip the claim — never ship an unfunded warranty. */
        if (!action) {
          for (var i = 0; i < items.length; i++) {
            var it = items[i];
            if (isWarrantyItem(it)) continue;
            var sku = it.properties && it.properties[PROP_SKU];
            if (!sku || String(sku).trim() === "") continue;
            if (actual.has(pairKey(it.id, sku))) continue;

            var props = Object.assign({}, it.properties);
            WARRANTY_PROP_KEYS.forEach(function (key) {
              delete props[key];
            });
            action = { line: i + 1, quantity: it.quantity, properties: props };
            break;
          }
        }

        if (!action) break;

        cart = await mbChangeLine(action);
        changed = true;
        if (increases) costIncreased = true;
      }

      if (changed) {
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      }
    } finally {
      mbReconcileLock = false;
    }

    return { changed: changed, costIncreased: costIncreased };
  }

  document.addEventListener("DOMContentLoaded", function () {
    mbScheduleReconcile(300);
  });
  document.addEventListener("product:added", function () {
    mbScheduleReconcile(400);
  });
  document.addEventListener("cart:refresh", function () {
    mbScheduleReconcile(400);
  });
  document.addEventListener(
    "click",
    function (e) {
      var trigger = e.target.closest
        ? e.target.closest(
            '[data-action="decrease-quantity"], [data-action="increase-quantity"], .mini-cart__quantity-remove, .line-item__quantity-remove'
          )
        : null;
      if (!trigger) return;
      mbScheduleReconcile(500);
    },
    true
  );

  /* Last line of defence: never let a mismatched cart reach checkout. */
  document.addEventListener(
    "click",
    function (e) {
      var btn = e.target.closest ? e.target.closest('[name="checkout"]') : null;
      if (!btn || btn.dataset.mbReconciled === "1") return;

      e.preventDefault();
      e.stopPropagation();
      btn.setAttribute("disabled", "disabled");

      mbReconcileCart()
        .catch(function () {
          return { changed: false, costIncreased: false };
        })
        .then(function (result) {
          btn.removeAttribute("disabled");

          /* If reconciliation ADDED cost, stop and let the shopper look at the
             updated cart rather than silently charging them more. */
          if (result && result.costIncreased) {
            showError(btn, "Your MaestroCare plan quantity was updated to match your items. Please review your cart.");
            return;
          }

          btn.dataset.mbReconciled = "1";
          btn.click();
        });
    },
    true
  );
})();


/* =========================================================================
 * Product gallery, desktop thumbnail rail: edge fades. September 2026.
 * -------------------------------------------------------------------------
 * Pairs with the block at the end of assets/mb-custom.css, which removed the
 * fixed max-height: 450px cap on .product-gallery__thumbnail-list so the rail
 * matches the height of the main image instead of being clipped mid-thumbnail.
 *
 * This script writes --mb-fade-top and --mb-fade-bottom on the rail, which the
 * mask in that CSS block reads, and adds data-mb-fade only when the rail is
 * actually scrollable.
 *
 * WHY THIS NEEDS SCRIPT AT ALL
 *
 * A fade painted purely in CSS would sit there permanently, including once the
 * shopper has scrolled to the last thumbnail, which tells them there is more
 * below when there is not. Scroll-driven CSS animations could do this without
 * script, but browser support is still uneven, so on a live storefront a scroll
 * listener is the safer call.
 *
 * Both values are graduated rather than on/off: each shrinks to 0 across the
 * final 56px of travel, so reaching either end of the rail feels like an ending
 * instead of a fade snapping away.
 *
 * WHAT IT LISTENS TO, AND WHY EACH ONE IS NEEDED
 *
 *   scroll   the shopper moving the rail.
 *   resize   the rail height follows the image, which follows the viewport.
 *   ResizeObserver   Flickity sets the gallery height after the images load,
 *                    so the rail's own box changes size after first paint.
 *   MutationObserver on class   this is the one that is easy to miss. Changing
 *                    colour on a product adds .is-filtered to the thumbnails
 *                    belonging to other variants, which changes scrollHeight
 *                    without changing the rail's own box, so a ResizeObserver
 *                    alone would never fire.
 *
 * The observer is filtered to the class attribute only, so the style and
 * data-mb-fade writes made by sync() cannot retrigger it into a loop.
 *
 * Everything is namespaced and additive. If this block is deleted the rail
 * keeps its new full height and simply loses the soft edge.
 * ========================================================================= */

(function () {
  "use strict";

  var FADE = 56; /* A little under one thumbnail row: 72px image + 16px margin. */
  var SELECTOR = ".product-gallery__thumbnail-list";

  function sync(list) {
    var overflow = list.scrollHeight - list.clientHeight;

    /* 1px of slack absorbs sub-pixel rounding on fractional device ratios. */
    if (overflow <= 1) {
      list.removeAttribute("data-mb-fade");
      return;
    }

    list.setAttribute("data-mb-fade", "");
    list.style.setProperty("--mb-fade-top", Math.min(FADE, list.scrollTop) + "px");
    list.style.setProperty("--mb-fade-bottom", Math.min(FADE, overflow - list.scrollTop) + "px");
  }

  function attach(list) {
    if (list.dataset.mbFadeBound) return;
    list.dataset.mbFadeBound = "true";

    var queued = false;
    function update() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        sync(list);
      });
    }

    list.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    if (window.ResizeObserver) {
      new ResizeObserver(update).observe(list);
    }

    if (window.MutationObserver) {
      new MutationObserver(update).observe(list, {
        attributes: true,
        subtree: true,
        attributeFilter: ["class"]
      });
    }

    update();
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach(attach);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  /* Covers the gallery settling after images load, and quick-view galleries
     that render from a <template> after first paint. attach() is idempotent. */
  window.addEventListener("load", init);
  document.addEventListener("modal:open", function () {
    setTimeout(init, 50);
  });
})();